import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  Chip
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { format } from 'date-fns';

const DeleteMaintenanceLogDialog = ({
  open,
  onClose,
  onSuccess,
  droneId,
  organizationUuid,
  droneName,
  maintenanceLog
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    if (!maintenanceLog) return;

    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${organizationUuid}/drones/${droneId}/maintenance/${maintenanceLog.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete maintenance log');
      }

      // Success - notify parent
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const getMaintenanceTypeColor = (type) => {
    const colors = {
      routine: 'info',
      repair: 'warning',
      inspection: 'default',
      calibration: 'primary',
      replacement: 'error',
      upgrade: 'success'
    };
    return colors[type] || 'default';
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <WarningIcon />
        Delete Maintenance Log
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <DialogContentText sx={{ mb: 2 }}>
          Are you sure you want to delete this maintenance log? This action cannot be undone.
        </DialogContentText>

        {maintenanceLog && (
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300'
            }}
          >
            {droneName && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Drone:</strong> {droneName}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2">
                <strong>Type:</strong>
              </Typography>
              <Chip
                label={maintenanceLog.maintenanceType}
                color={getMaintenanceTypeColor(maintenanceLog.maintenanceType)}
                size="small"
                sx={{ textTransform: 'capitalize' }}
              />
            </Box>
            <Typography variant="body2" gutterBottom>
              <strong>Date:</strong> {formatDate(maintenanceLog.performedDate)}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Description:</strong> {maintenanceLog.description}
            </Typography>
            {maintenanceLog.cost && (
              <Typography variant="body2" gutterBottom>
                <strong>Cost:</strong>{' '}
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: maintenanceLog.costCurrency || 'USD'
                }).format(maintenanceLog.cost)}
              </Typography>
            )}
            {maintenanceLog.performer && (
              <Typography variant="body2">
                <strong>Performed By:</strong>{' '}
                {maintenanceLog.performer.firstName} {maintenanceLog.performer.lastName}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={loading}
        >
          {loading ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteMaintenanceLogDialog;
